import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Users, UserPlus, Filter, Search, X,
    RefreshCcw, Send, Rocket, CheckCircle2, Info,
    Edit2, Trash2, Plus
} from 'lucide-react';
import api from '../api/api';
import { toast } from 'sonner';
import { useConfirm } from '../contexts/ConfirmContext';
import SequenceEnrollModal from './SequenceEnrollModal';
import AddToCampaignModal from './AddToCampaignModal';
import PageShell from './ui/PageShell';
import PageHeader from './ui/PageHeader';
import DataTable from './ui/DataTable';
import EmptyState from './ui/EmptyState';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
    SheetFooter,
} from './ui/sheet';

export default function GroupDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const confirm = useConfirm();

    // Group data
    const [group, setGroup] = useState(null);
    const [loading, setLoading] = useState(true);

    // Members
    const [members, setMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(false);
    const [memberTotal, setMemberTotal] = useState(0);
    const [memberPage, setMemberPage] = useState(1);
    const [memberLimit, setMemberLimit] = useState(50);
    const [memberTotalPages, setMemberTotalPages] = useState(1);
    const [memberSearch, setMemberSearch] = useState('');
    const [selectedMemberIds, setSelectedMemberIds] = useState([]);

    // Add contacts modal
    const [isAddContactOpen, setIsAddContactOpen] = useState(false);
    const [availableContacts, setAvailableContacts] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(false);
    const [selectedContactIds, setSelectedContactIds] = useState(new Set());
    const [contactSearch, setContactSearch] = useState('');

    // Edit modal
    const [isEditOpen, setIsEditOpen] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');
    const [editType, setEditType] = useState('STATIC');
    const [editRuleConfig, setEditRuleConfig] = useState({ filters: [] });
    const [previewCount, setPreviewCount] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    // Bulk action modals
    const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
    const [isAddCampaignOpen, setIsAddCampaignOpen] = useState(false);

    // ─── Fetch group ──────────────────────────────────────
    const fetchGroup = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get(`/api/groups/${id}`);
            setGroup(res.data);
        } catch (err) {
            toast.error('Group not found');
            navigate('/groups');
        } finally {
            setLoading(false);
        }
    }, [id, navigate]);

    useEffect(() => { fetchGroup(); }, [fetchGroup]);

    // ─── Fetch members ────────────────────────────────────
    const fetchMembers = useCallback(async () => {
        if (!id) return;
        setMembersLoading(true);
        try {
            const res = await api.get(`/api/groups/${id}/members`, {
                params: {
                    page: memberPage,
                    limit: memberLimit,
                    search: memberSearch
                }
            });
            setMembers(res.data.data || []);
            setMemberTotal(res.data.pagination?.total || 0);
            setMemberTotalPages(res.data.pagination?.totalPages || 1);
        } catch {
            toast.error('Failed to load members');
        } finally {
            setMembersLoading(false);
        }
    }, [id, memberPage, memberLimit, memberSearch]);

    useEffect(() => {
        if (group) fetchMembers();
    }, [group, fetchMembers]);

    // Reset page on search
    const handleSearchChange = (val) => {
        setMemberSearch(val);
        setMemberPage(1);
    };

    // ─── Remove member ────────────────────────────────────
    const handleRemoveMember = async (contactId) => {
        const ok = await confirm('Remove from Group?', 'Remove this contact from the group? The contact will not be deleted.');
        if (!ok) return;
        try {
            await api.delete(`/api/groups/${id}/members`, { data: { workspaceContactIds: [contactId] } });
            fetchMembers();
            fetchGroup();
            toast.success('Member removed from group');
        } catch {
            toast.error('Failed to remove member');
        }
    };

    // ─── Bulk remove ──────────────────────────────────────
    const handleBulkRemoveMembers = async () => {
        if (selectedMemberIds.length === 0) return;
        const ok = await confirm('Remove Selected?', `Remove ${selectedMemberIds.length} members from this group?`);
        if (!ok) return;
        try {
            await api.delete(`/api/groups/${id}/members`, { data: { workspaceContactIds: selectedMemberIds } });
            setSelectedMemberIds([]);
            fetchMembers();
            fetchGroup();
            toast.success(`${selectedMemberIds.length} members removed`);
        } catch {
            toast.error('Failed to remove members');
        }
    };

    // ─── Add contacts ─────────────────────────────────────
    const openAddContactModal = () => {
        setSelectedContactIds(new Set());
        setContactSearch('');
        setIsAddContactOpen(true);
        fetchAvailableContacts('');
    };

    const fetchAvailableContacts = async (q) => {
        setContactsLoading(true);
        try {
            const res = await api.get(`/api/contacts?limit=60&search=${encodeURIComponent(q)}`);
            setAvailableContacts(res.data.data || []);
        } catch { /* noop */ }
        finally { setContactsLoading(false); }
    };

    useEffect(() => {
        if (!isAddContactOpen) return;
        const t = setTimeout(() => fetchAvailableContacts(contactSearch), 300);
        return () => clearTimeout(t);
    }, [contactSearch, isAddContactOpen]);

    const toggleContact = (id) => setSelectedContactIds(prev => {
        const next = new Set(prev);
        next.has(id) ? next.delete(id) : next.add(id);
        return next;
    });

    const handleAddSelectedContacts = async () => {
        if (selectedContactIds.size === 0) return;
        try {
            await api.post(`/api/groups/${id}/members`, { workspaceContactIds: Array.from(selectedContactIds) });
            setIsAddContactOpen(false);
            fetchMembers();
            fetchGroup();
            toast.success(`${selectedContactIds.size} contact${selectedContactIds.size > 1 ? 's' : ''} added`);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to add contacts');
        }
    };

    // ─── Edit group ───────────────────────────────────────
    const openEditModal = () => {
        setEditName(group.name);
        setEditDescription(group.description || '');
        setEditType(group.type || 'STATIC');
        setEditRuleConfig(group.ruleConfig || { filters: [] });
        setPreviewCount(null);
        setIsEditOpen(true);
    };

    const addFilter = () => setEditRuleConfig(p => ({ ...p, filters: [...p.filters, { field: 'stage', operator: 'equals', value: '' }] }));
    const updateFilter = (i, k, v) => {
        const f = [...editRuleConfig.filters];
        f[i] = { ...f[i], [k]: v };
        setEditRuleConfig({ ...editRuleConfig, filters: f });
    };
    const removeFilter = (i) => setEditRuleConfig(p => ({ ...p, filters: p.filters.filter((_, idx) => idx !== i) }));

    const handlePreviewCount = async () => {
        if (previewLoading) return;
        setPreviewLoading(true); setPreviewCount(null);
        const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000));
        try {
            const res = await Promise.race([
                api.post('/api/groups/preview-count', { ruleConfig: editRuleConfig }),
                timeout
            ]);
            setPreviewCount(res.data.count ?? 0);
        } catch (err) {
            setPreviewCount(err.message === 'timeout' ? 'Timeout' : 'Error');
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSaveEdit = async () => {
        if (!editName.trim()) { toast.warning('Group name is required'); return; }
        setIsSaving(true);
        try {
            const res = await api.put(`/api/groups/${id}`, {
                name: editName,
                description: editDescription,
                type: editType,
                ruleConfig: editRuleConfig
            });
            setGroup(res.data);
            setIsEditOpen(false);
            toast.success('Group updated');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to save');
        } finally {
            setIsSaving(false);
        }
    };

    // ─── Delete group ─────────────────────────────────────
    const handleDelete = async () => {
        const ok = await confirm(
            'Delete Group?',
            `Delete "${group?.name}"? This removes all memberships but DOES NOT delete the contacts.`
        );
        if (!ok) return;
        try {
            await api.delete(`/api/groups/${id}`);
            toast.success('Group deleted');
            navigate('/groups');
        } catch (err) {
            toast.error(err.response?.data?.error || 'Failed to delete');
        }
    };

    // ─── Loading ──────────────────────────────────────────
    if (loading) {
        return (
            <PageShell>
                <div className="animate-pulse space-y-8">
                    <div className="bg-muted h-24 rounded-[2rem]" />
                    <div className="bg-muted h-96 rounded-[2.5rem]" />
                </div>
            </PageShell>
        );
    }

    if (!group) return null;

    const isDynamic = group.type === 'DYNAMIC';

    return (
        <PageShell>
            <PageHeader
                title={group.name}
                subtitle={group.description || (isDynamic ? 'Dynamic group · rule-driven membership' : 'Static group · manually managed')}
                icon={Users}
                actions={
                    <div className="flex items-center gap-3 flex-wrap">
                        <Button variant="ghost" onClick={() => navigate('/groups')}
                            className="h-10 px-4 rounded-xl font-black text-[10px] uppercase tracking-widest border-2 border-border/50 flex items-center gap-2">
                            <ArrowLeft className="w-4 h-4" /> All Groups
                        </Button>
                        <Button variant="ghost" onClick={openEditModal}
                            className="h-10 px-4 rounded-xl font-black text-[10px] uppercase tracking-widest border-2 border-border/50 flex items-center gap-2">
                            <Edit2 className="w-3.5 h-3.5" /> Edit
                        </Button>
                        <Button variant="ghost" onClick={handleDelete}
                            className="h-10 px-4 rounded-xl font-black text-[10px] uppercase tracking-widest border-2 border-destructive/20 text-destructive hover:bg-destructive hover:text-white flex items-center gap-2">
                            <Trash2 className="w-3.5 h-3.5" /> Delete Group
                        </Button>
                        {!isDynamic && (
                            <Button onClick={openAddContactModal}
                                className="h-10 px-5 rounded-xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-primary/20 flex items-center gap-2">
                                <UserPlus className="w-3.5 h-3.5" /> Add Members
                            </Button>
                        )}
                    </div>
                }
            />

            {/* Dynamic rule banner */}
            {isDynamic && (
                <div className="mb-6 bg-indigo-500/5 border border-indigo-500/20 rounded-2xl p-4 flex items-start gap-3">
                    <Filter className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mb-1.5">Live Rules Active</p>
                        {group.ruleConfig?.filters?.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                                {group.ruleConfig.filters.map((f, i) => (
                                    <span key={i} className="text-[10px] font-bold bg-indigo-500/10 text-indigo-700 border border-indigo-500/20 px-2 py-0.5 rounded-lg">
                                        {f.field} {f.operator === 'equals' ? '=' : f.operator === 'not_equals' ? '≠' : '~'} "{f.value}"
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <p className="text-xs text-indigo-600/70 font-medium">No filters — matches all workspace contacts.</p>
                        )}
                    </div>
                    <span className="text-[10px] font-black text-indigo-600 bg-indigo-500/10 border border-indigo-500/20 px-3 py-1 rounded-full shrink-0">
                        {group.memberCount ?? memberTotal} members
                    </span>
                </div>
            )}

            {/* Dynamic member-list caveat */}
            {isDynamic && (
                <div className="mb-4 bg-amber-500/5 border border-amber-500/20 rounded-2xl p-4 flex items-start gap-3">
                    <Info className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-700/80 font-medium leading-relaxed">
                        Member count above is computed live from your rules. The table below shows contacts <strong>manually added</strong> to this group.
                    </p>
                </div>
            )}

            {/* Bulk action bar */}
            {selectedMemberIds.length > 0 && (
                <div className="mb-5 bg-card border-2 border-primary/20 rounded-2xl px-5 py-3.5 flex items-center justify-between shadow-lg shadow-primary/5 animate-in slide-in-from-top-2 duration-300">
                    <div className="flex items-center gap-3">
                        <div className="w-7 h-7 bg-primary rounded-xl flex items-center justify-center">
                            <CheckCircle2 className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-black text-sm">{selectedMemberIds.length} selected</span>
                        <button onClick={() => setSelectedMemberIds([])} className="text-[10px] font-black text-muted-foreground uppercase tracking-widest hover:text-foreground transition-colors">Clear</button>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <Button size="sm" variant="ghost" onClick={() => setIsEnrollModalOpen(true)}
                            className="h-8 px-3 rounded-xl font-black text-[10px] uppercase tracking-widest border border-violet-500/20 text-violet-600 hover:bg-violet-500/10">
                            <Send className="w-3.5 h-3.5 mr-1.5" /> Add to Sequence
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => setIsAddCampaignOpen(true)}
                            className="h-8 px-3 rounded-xl font-black text-[10px] uppercase tracking-widest border border-indigo-500/20 text-indigo-600 hover:bg-indigo-500/10">
                            <Rocket className="w-3.5 h-3.5 mr-1.5" /> Add to Campaign
                        </Button>
                        {!isDynamic && (
                            <Button size="sm" variant="ghost" onClick={handleBulkRemoveMembers}
                                className="h-8 px-3 rounded-xl font-black text-[10px] uppercase tracking-widest border border-destructive/20 text-destructive hover:bg-destructive/10">
                                <X className="w-3.5 h-3.5 mr-1.5" /> Remove from Group
                            </Button>
                        )}
                    </div>
                </div>
            )}

            {/* Members table */}
            <div className="bg-card border border-border rounded-[2.5rem] shadow-sm overflow-hidden">
                <DataTable
                    columns={[
                        {
                            header: 'Contact',
                            accessor: 'identity',
                            render: (m) => (
                                <div>
                                    <p className="font-bold text-sm text-foreground">
                                        {m.identity.firstName} {m.identity.lastName}
                                    </p>
                                    <p className="text-[11px] text-muted-foreground font-medium mt-0.5">{m.identity.displayEmail}</p>
                                </div>
                            )
                        },
                        {
                            header: 'Added',
                            accessor: 'addedAt',
                            align: 'right',
                            render: (m) => (
                                <div className="flex items-center justify-end gap-3">
                                    <span className="text-[11px] text-muted-foreground font-medium">
                                        {m.addedAt ? new Date(m.addedAt).toLocaleDateString() : '—'}
                                    </span>
                                    {!isDynamic && (
                                        <button
                                            onClick={() => handleRemoveMember(m.workspaceContactId)}
                                            className="opacity-0 group-hover:opacity-100 transition-opacity p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                                            title="Remove from Group"
                                        >
                                            <X className="w-3.5 h-3.5" />
                                        </button>
                                    )}
                                </div>
                            )
                        }
                    ]}
                    data={members}
                    loading={membersLoading}
                    pagination={{
                        page: memberPage,
                        limit: memberLimit,
                        totalRecords: memberTotal,
                        totalPages: memberTotalPages
                    }}
                    onPageChange={setMemberPage}
                    onLimitChange={(v) => { setMemberLimit(v); setMemberPage(1); }}
                    search={{
                        placeholder: 'Search members...',
                        value: memberSearch,
                        onChange: handleSearchChange
                    }}
                    selectable={!isDynamic ? {
                        selectedIds: selectedMemberIds,
                        onSelectionChange: setSelectedMemberIds,
                        rowId: (row) => row.workspaceContactId?.toString()
                    } : undefined}
                    emptyState={
                        <EmptyState
                            title={isDynamic ? 'No contacts match your rules' : 'No members yet'}
                            description={isDynamic
                                ? 'Adjust your dynamic group rules to include contacts.'
                                : 'Add workspace contacts to this group using the Add Members button.'}
                            icon={Users}
                            action={!isDynamic
                                ? <button onClick={openAddContactModal} className="text-primary font-black text-sm hover:underline">Add Members →</button>
                                : undefined}
                        />
                    }
                />
            </div>

            {/* ── Edit Group Dialog ── */}
            <Sheet open={isEditOpen} onOpenChange={setIsEditOpen}>
                <SheetContent side="right" className="rounded-[2.5rem] border-2 border-border/40 shadow-2xl max-w-lg overflow-hidden flex flex-col max-h-[90vh]">
                    <SheetHeader className="px-6 pt-6">
                        <SheetTitle className="text-xl font-black tracking-tight">Edit Group</SheetTitle>
                        <SheetDescription className="text-xs text-muted-foreground font-medium uppercase tracking-widest">Update group profile and audience rules.</SheetDescription>
                    </SheetHeader>
                    
                    <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
                        <div>
                            <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2">Group Name</label>
                            <input
                                type="text" value={editName}
                                onChange={e => setEditName(e.target.value)}
                                className="w-full bg-muted/30 border-2 border-border/50 text-foreground rounded-2xl focus:border-primary font-bold py-3.5 px-5 transition-all outline-none text-sm"
                            />
                        </div>
                        <div>
                            <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2">Description</label>
                            <textarea
                                value={editDescription} rows={2}
                                onChange={e => setEditDescription(e.target.value)}
                                className="w-full bg-muted/30 border-2 border-border/50 text-foreground rounded-2xl focus:border-primary font-medium py-3.5 px-5 transition-all outline-none text-sm resize-none"
                            />
                        </div>

                        <div>
                            <label className="block text-[10px] font-black text-muted-foreground uppercase tracking-widest mb-2">Group Type</label>
                            <div className="flex gap-2 p-1.5 bg-muted/30 border-2 border-border/50 rounded-2xl">
                                {[
                                    { id: 'STATIC', label: 'Static', icon: <Users className="w-3.5 h-3.5" /> },
                                    { id: 'DYNAMIC', label: 'Dynamic', icon: <Filter className="w-3.5 h-3.5" /> }
                                ].map(t => (
                                    <button key={t.id} onClick={() => setEditType(t.id)}
                                        className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-xl transition-all ${editType === t.id ? 'bg-background text-primary shadow-sm border border-primary/10' : 'text-muted-foreground hover:text-foreground'}`}>
                                        <span className="text-[10px] font-black uppercase tracking-widest">{t.icon} {t.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {editType === 'DYNAMIC' && (
                            <div className="space-y-3 animate-in slide-in-from-top-2 duration-300">
                                <div className="flex items-center justify-between">
                                    <label className="text-[10px] font-black text-muted-foreground uppercase tracking-widest">Rules</label>
                                    <button onClick={addFilter} className="text-primary text-[10px] font-black uppercase tracking-widest flex items-center gap-1 hover:underline">
                                        <Plus className="w-3 h-3" /> Add Rule
                                    </button>
                                </div>

                                <div className="space-y-2">
                                    {editRuleConfig.filters.length === 0 ? (
                                        <div className="p-4 border-2 border-dashed border-border/50 rounded-2xl text-center">
                                            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">No rules — matches all workspace contacts</p>
                                        </div>
                                    ) : (
                                        editRuleConfig.filters.map((f, i) => (
                                            <div key={i} className="flex gap-2 items-center bg-muted/20 p-3 rounded-2xl border border-border/50">
                                                <Select value={f.field} onValueChange={v => updateFilter(i, 'field', v)}>
                                                    <SelectTrigger className="flex-1 h-9 bg-background border-2 border-border/50 rounded-xl text-xs font-bold">
                                                        <SelectValue placeholder="Field" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-card border-border rounded-xl shadow-xl z-[400]">
                                                        <SelectItem value="stage" className="font-bold text-xs cursor-pointer">Stage</SelectItem>
                                                        <SelectItem value="status" className="font-bold text-xs cursor-pointer">Status</SelectItem>
                                                        <SelectItem value="tags" className="font-bold text-xs cursor-pointer">Tags</SelectItem>
                                                    </SelectContent>
                                                </Select>

                                                <Select value={f.operator} onValueChange={v => updateFilter(i, 'operator', v)}>
                                                    <SelectTrigger className="w-24 h-9 bg-background border-2 border-border/50 rounded-xl text-xs font-bold">
                                                        <SelectValue placeholder="Op" />
                                                    </SelectTrigger>
                                                    <SelectContent className="bg-card border-border rounded-xl shadow-xl z-[400]">
                                                        <SelectItem value="equals" className="font-bold text-xs cursor-pointer">= equals</SelectItem>
                                                        <SelectItem value="not_equals" className="font-bold text-xs cursor-pointer">≠ not</SelectItem>
                                                        <SelectItem value="contains" className="font-bold text-xs cursor-pointer">~ contains</SelectItem>
                                                    </SelectContent>
                                                </Select>

                                                {/* Value adaptive dropdown */}
                                                {f.field === 'stage' ? (
                                                    <Select value={f.value} onValueChange={v => updateFilter(i, 'value', v)}>
                                                        <SelectTrigger className="flex-1 h-9 bg-background border-2 border-border/50 rounded-xl text-xs font-bold">
                                                            <SelectValue placeholder="Value" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-card border-border rounded-xl shadow-xl z-[400]">
                                                            <SelectItem value="LEAD" className="font-bold text-xs cursor-pointer">Lead</SelectItem>
                                                            <SelectItem value="CONTACTED" className="font-bold text-xs cursor-pointer">Contacted</SelectItem>
                                                            <SelectItem value="REPLIED" className="font-bold text-xs cursor-pointer">Replied</SelectItem>
                                                            <SelectItem value="MEETING_BOOKED" className="font-bold text-xs cursor-pointer">Meeting Booked</SelectItem>
                                                            <SelectItem value="CLOSED_WON" className="font-bold text-xs cursor-pointer">Won</SelectItem>
                                                            <SelectItem value="CLOSED_LOST" className="font-bold text-xs cursor-pointer">Lost</SelectItem>
                                                            <SelectItem value="UNQUALIFIED" className="font-bold text-xs cursor-pointer">Unqualified</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                ) : f.field === 'status' ? (
                                                    <Select value={f.value} onValueChange={v => updateFilter(i, 'value', v)}>
                                                        <SelectTrigger className="flex-1 h-9 bg-background border-2 border-border/50 rounded-xl text-xs font-bold">
                                                            <SelectValue placeholder="Value" />
                                                        </SelectTrigger>
                                                        <SelectContent className="bg-card border-border rounded-xl shadow-xl z-[400]">
                                                            <SelectItem value="VERIFIED" className="font-bold text-xs cursor-pointer">Verified</SelectItem>
                                                            <SelectItem value="RISKY" className="font-bold text-xs cursor-pointer">Risky</SelectItem>
                                                            <SelectItem value="INVALID" className="font-bold text-xs cursor-pointer">Invalid</SelectItem>
                                                            <SelectItem value="UNKNOWN" className="font-bold text-xs cursor-pointer">Unknown</SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                ) : (
                                                    <input
                                                        type="text" value={f.value}
                                                        onChange={e => updateFilter(i, 'value', e.target.value)}
                                                        placeholder="Value"
                                                        className="flex-1 h-9 bg-background border-2 border-border/50 text-xs font-bold px-3 rounded-xl outline-none"
                                                    />
                                                )}

                                                <button onClick={() => removeFilter(i)} className="text-muted-foreground hover:text-destructive shrink-0 p-1.5 hover:bg-destructive/10 rounded-lg">
                                                    <X className="w-3.5 h-3.5" />
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>

                                <button onClick={handlePreviewCount}
                                    className="w-full flex items-center justify-between bg-primary/5 border border-primary/20 rounded-2xl px-5 py-3 hover:bg-primary/10 transition-colors">
                                    <span className="text-[10px] font-black text-primary uppercase tracking-widest flex items-center gap-2">
                                        <Users className="w-3.5 h-3.5" /> Preview Match Count
                                    </span>
                                    {previewLoading ? (
                                        <RefreshCcw className="w-3.5 h-3.5 text-primary animate-spin" />
                                    ) : (
                                        <span className="text-sm font-black text-primary">{previewCount !== null ? previewCount : '...'}</span>
                                    )}
                                </button>
                            </div>
                        )}
                    </div>

                    <SheetFooter className="px-6 py-6 border-t border-border/40 bg-muted/10">
                        <Button variant="ghost" onClick={() => setIsEditOpen(false)} className="h-12 rounded-2xl border-2 border-border/40 font-black text-[10px] uppercase tracking-widest flex-1">Cancel</Button>
                        <Button disabled={isSaving || !editName.trim()} onClick={handleSaveEdit}
                            className="h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-primary/20 flex-[2]">
                            {isSaving ? <RefreshCcw className="w-4 h-4 animate-spin mr-2" /> : null}
                            Save Changes
                        </Button>
                    </SheetFooter>
                </SheetContent>
            </Sheet>

            {/* ── Add Contacts Modal ── */}
            {isAddContactOpen && (
                <div className="fixed inset-0 bg-background/80 backdrop-blur-xl z-[100] flex items-center justify-center p-4 animate-in fade-in duration-300">
                    <div className="bg-card border-2 border-border/40 w-full max-w-2xl rounded-[2.5rem] shadow-[0_40px_100px_rgba(0,0,0,0.1)] overflow-hidden flex flex-col max-h-[90vh]">
                        <div className="p-7 border-b border-border/40 flex items-center justify-between bg-muted/10">
                            <div className="flex items-center gap-4">
                                <div className="w-11 h-11 bg-primary/10 rounded-2xl flex items-center justify-center text-primary shadow-inner">
                                    <UserPlus className="w-5 h-5" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-black tracking-tight">Add Members</h3>
                                    <p className="text-muted-foreground text-[10px] uppercase font-black tracking-widest mt-0.5">
                                        {selectedContactIds.size > 0 ? `${selectedContactIds.size} selected` : 'Select workspace contacts'}
                                    </p>
                                </div>
                            </div>
                            <button onClick={() => setIsAddContactOpen(false)} className="p-2.5 hover:bg-destructive hover:text-white rounded-xl transition-all group">
                                <X className="w-5 h-5 group-hover:rotate-90 transition-transform" />
                            </button>
                        </div>

                        <div className="px-7 pt-5 pb-3">
                            <div className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                <input
                                    type="text" autoFocus value={contactSearch}
                                    onChange={e => setContactSearch(e.target.value)}
                                    placeholder="Search by name or email..."
                                    className="w-full pl-11 pr-4 h-11 bg-muted/30 border-2 border-border/50 rounded-2xl font-semibold text-sm outline-none focus:border-primary transition-colors"
                                />
                            </div>
                        </div>

                        <div className="flex-1 overflow-y-auto px-7 pb-5">
                            {contactsLoading ? (
                                <div className="py-12 flex items-center justify-center">
                                    <RefreshCcw className="w-8 h-8 text-primary animate-spin opacity-40" />
                                </div>
                            ) : availableContacts.length === 0 ? (
                                <div className="py-12 text-center">
                                    <p className="text-sm font-black text-muted-foreground opacity-50 uppercase tracking-widest">No contacts found</p>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2">
                                    {availableContacts.map(c => {
                                        const isSelected = selectedContactIds.has(c.id);
                                        return (
                                            <div key={c.id} onClick={() => toggleContact(c.id)}
                                                className={`p-4 border-2 rounded-2xl flex items-center gap-3 cursor-pointer transition-all ${isSelected ? 'bg-primary/5 border-primary shadow-sm' : 'bg-background border-border/50 hover:border-primary/40'}`}>
                                                <div className={`w-5 h-5 rounded-lg border-2 flex items-center justify-center shrink-0 transition-colors ${isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'}`}>
                                                    {isSelected && <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
                                                </div>
                                                <div className="min-w-0">
                                                    <p className={`text-sm font-bold truncate ${isSelected ? 'text-primary' : 'text-foreground'}`}>{c.displayName}</p>
                                                    <p className="text-[10px] text-muted-foreground font-medium truncate">{c.displayEmail}</p>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        <div className="p-7 border-t border-border/40 bg-muted/10 flex gap-4">
                            <Button variant="ghost" onClick={() => setIsAddContactOpen(false)} className="flex-1 h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest border-2 border-border/40">Cancel</Button>
                            <Button disabled={selectedContactIds.size === 0} onClick={handleAddSelectedContacts}
                                className="flex-[2] h-12 rounded-2xl font-black text-[10px] uppercase tracking-widest shadow-lg shadow-primary/20 flex items-center justify-center gap-2">
                                <UserPlus className="w-4 h-4" /> Add {selectedContactIds.size > 0 ? selectedContactIds.size : ''} Member{selectedContactIds.size !== 1 ? 's' : ''}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Sequence modal */}
            {isEnrollModalOpen && (
                <SequenceEnrollModal
                    workspaceContactIds={selectedMemberIds}
                    onClose={() => setIsEnrollModalOpen(false)}
                    onComplete={() => { setIsEnrollModalOpen(false); setSelectedMemberIds([]); }}
                />
            )}

            {/* Campaign modal */}
            {isAddCampaignOpen && (
                <AddToCampaignModal
                    workspaceContactIds={selectedMemberIds}
                    onClose={() => setIsAddCampaignOpen(false)}
                    onComplete={() => { setIsAddCampaignOpen(false); setSelectedMemberIds([]); }}
                />
            )}
        </PageShell>
    );
}
